import { JSEncrypt } from 'jsencrypt'
import { DataDicGroup, DeliveryMode, Lazy, LogisticsType, ModalApi, OutOrderGoodStatus, StaffPost, StoreActivityType, StoreType, MallOrderStatus, MarketingScoped, ActivityTag } from './type'
import { message, Modal, notification } from 'ant-design-vue'
import { Ref } from 'vue'
import dayjs from 'dayjs'
import { AuthDictionary } from './auth'
// 加密对象，lazy模式
const pwdEncrypt = new Lazy(() => {
  // 公钥
  const publicKey = 'MFwwDQYJKoZIhvcNAQEBBQADSwAwSAJBAPWkppjcemqum59WKpWAADIyOE5X8A278qd3edRhXi023YViwc1N5yP7xt4ableeS3J0GwZdttmdf6fCT0LDt6kCAwEAAQ=='
  const jsEncrypt = new JSEncrypt()
  jsEncrypt.setPublicKey(publicKey)
  return jsEncrypt
})
/**
 * 加密
 * @param content 加密内容
 */
function encrypt(content: string) {
  return pwdEncrypt.value.encrypt(content)
}

// 配置提示信息统一为1.5秒
message.config({
  duration: 1.5
})

// form表单验证
const required = (message: string, trigger = ['blur', 'change']) => ({ required: true, message, trigger })

// 对象克隆
function clone<T>(obj: T): T { return JSON.parse(JSON.stringify(obj)) }

// 获取商户类型名称
const getStoreTypeName = (type: StoreType) => type === StoreType.Normal ? '普通商户' : '连锁总店'

// 压缩图片
const compress = (img: any, theW: number, theH: number, realW: number, realH: number) => {
  let canvas = document.createElement("canvas")
  canvas.width = theW
  canvas.height = theH
  let $image = document.createElement('img') as HTMLImageElement
  $image.src = img
  let context = canvas.getContext("2d") as CanvasRenderingContext2D
  context.drawImage($image, 0, 0, realW, realH, 0, 0, theW, theH)

  return new Promise<{ data: Blob, src: string }>((resolve) => {
    canvas.toBlob((data) => {
      resolve({ data: data as Blob, src: canvas.toDataURL('image/png') })
    })
  })
}

/**
 * 获取文件的Base64编码
 * @param file 文件
 * @returns 
 */
const getBase64 = (file: File) => {
  return new Promise((resolve: (base64: string) => void, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
  })
}

/**
 * 生成id
 */
const genId = (mutil: boolean = false) => {
  let code = Number(Math.random().toString().substring(3, 10) + Date.now()).toString(36)
  return mutil ? (code + genId()) : code
}

/**
 * 生成一个GUID 
 */
const genGuid = () => {
  let uuidValue = ""
  let randomValue: number
  for (let k = 0; k < 32; k++) {
    randomValue = Math.random() * 16 | 0

    if (k == 8 || k == 12 || k == 16 || k == 20) {
      uuidValue += "-"
    }
    uuidValue += (k == 12 ? 4 : (k == 16 ? (randomValue & 3 | 8) : randomValue)).toString(16)
  }
  return uuidValue
}

/**
 * 获取当前日期
 * @returns 
 */
const getDate = () => new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + new Date().getDate()

/**获取当前时间 */
const getNowTime = () => dayjs().format('YYYY-MM-DD HH:mm:ss')

/**
 * 日期转化为生日
 * @param birthday 生日文本
 * @returns 
 */
const parseBirthday = (birthday: string | null) => {
  if (!birthday) return ''
  return birthday.substring(0, 10)
}

/**
 * 日期转化为年龄
 * @param birthday 生日文本
 * @returns 
 */
const parseAge = (birthday: string | null) => {
  if (!birthday) return ''
  return ((Date.now() - Date.parse(parseBirthday(birthday))) / 1000 / 60 / 60 / 24 / 365).toFixed(0) + '岁'
}
/**
 * 转化为性别描述
 * @param gender
 * @returns 
 */
const parseGender = (gender: number) => {
  if (gender === 1) return '男'
  if (gender === 2) return '女'
  return '未知'
}
/**
 * 转化为性别描述
 * @param gender
 * @returns 
 */
const parseGenderColor = (gender: number) => {
  if (gender === 1) return 'blue'
  if (gender === 2) return 'pink'
  return 'gray'
}

const postFlags = {
  '1': 'blue',
  '2': 'green',
  '4': 'purple',
  '8': 'red'
}
/**
 * 转化为岗位数组
 * @param post 岗位
 */
const parsePost = (post: number) => {
  const arr = []
  if (post & StaffPost.manager) arr.push({ name: '管理员', color: postFlags[StaffPost.manager] })
  if (post & StaffPost.waiter) arr.push({ name: '服务员', color: postFlags[StaffPost.waiter] })
  if (post & StaffPost.cashier) arr.push({ name: '收银员', color: postFlags[StaffPost.cashier] })
  if (post & StaffPost.cooker) arr.push({ name: '厨师', color: postFlags[StaffPost.cooker] })
  return arr
}

// 页面加载
let pageloading: Ref<boolean>
const loading = (val: boolean = true) => pageloading.value = val
const setLoading = (val: Ref<boolean>) => pageloading = val

/**
 * 获取查询时间段（发送后台时格式化数据）
 * @param time 
 * @returns 
 */
const getTimespan = (time: HuaskyTime): HuaskyTime => {
  const obj = clone(time)
  if (time.type === 1) {                // 当天
    obj.startTime = dayjs().format('YYYY-MM-DD')
    obj.endTime = dayjs().add(1, 'day').format('YYYY-MM-DD')
  } else if (time.type === 2) {         // 两日内
    obj.startTime = dayjs().add(-1, 'day').format('YYYY-MM-DD')
    obj.endTime = dayjs().add(1, 'day').format('YYYY-MM-DD')
  } else if (time.type === 3) {         // 当月
    obj.startTime = dayjs().add(-new Date().getDate() + 1, 'day').format('YYYY-MM-DD')
    obj.endTime = dayjs().add(1, 'day').format('YYYY-MM-DD')
  } else if (time.type === 4) {         // 近一个月
    obj.startTime = dayjs().add(-1, 'month').add(1, 'day').format('YYYY-MM-DD')
    obj.endTime = dayjs().add(1, 'day').format('YYYY-MM-DD')
  } else if (time.type === 5) {         // 午市
    const noon = getSearchTime(JSON.parse($app.dic?.storeTime.info as string).noon)
    obj.startTime = noon[0]
    obj.endTime = noon[1]
  } else if (time.type === 6) {         // 晚市
    const afternoon = getSearchTime(JSON.parse($app.dic?.storeTime.info as string).afternoon)
    obj.startTime = afternoon[0]
    obj.endTime = afternoon[1]
  } else if (time.type === 7) {         // 昨天
    obj.startTime = dayjs().add(-1, 'day').format('YYYY-MM-DD')
    obj.endTime = dayjs().format('YYYY-MM-DD')
  } else if (time.type === 0) {
    obj.startTime = undefined
    obj.endTime = undefined
  }
  return obj
}
function getSearchTime(times: [string, string]): [string, string] {
  const startTime = dayjs(times[0], 'HH:mm:ss')
  const endTime = dayjs(times[1], 'HH:mm:ss')
  if (startTime > endTime) {
    endTime.add(1, 'day')
  }
  return [startTime.format('YYYY-MM-DD HH:mm:ss'), endTime.format('YYYY-MM-DD HH:mm:ss')]
}

/**
 * 格式化金额
 * @param amount 
 * @returns 
 */
const formatAmount = (amount) => {
  if (amount == 0) return '-'
  return '￥' + amount
}

/**
 * 获取订单状态描述
 * @param status 
 * @returns 
 */
const getOrderStatusText = (status: number) => {
  switch (status) {
    case 1: return '正在点单'
    case 2: return '用餐中'
    case 4: return '已结算'
    case 8: return '反结算'
    case 16: return '已取消'
    case 32: return '已退款'
    case 64: return '用户已提交'
  }
  return ''
}
/**
 * 获取订单状态对应的颜色
 * @param status 
 * @returns 
 */
const getOrderStatusColor = (status: number) => {
  switch (status) {
    case 1: return 'pink'
    case 2: return 'green'
    case 4: return 'blue'
    case 8: return 'red'
    case 16: return 'gray'
    case 32: return 'purple'
    case 64: return 'orange'
  }
  return ''
}

/**
 * 获取订单来源描述
 * @param source 
 * @returns 
 */
const getOrderSourceText = (source: number) => {
  switch (source) {
    case 1: return '外卖'
    case 2: return '扫码点餐'
    case 3: return '收银台'
  }
  return ''

}
/**
 * 获取订单来源颜色
 * @param source 
 * @returns 
 */
const getOrderSourceColor = (source: number) => {
  switch (source) {
    case 1: return 'teal'
    case 2: return 'olive'
    case 3: return 'maroon'
  }
  return ''
}


/**
 * 获取商品来源描述
 * @param source 
 * @returns 
 */
const getGoodStatusText = (source: number) => {
  switch (source) {
    case 1: return '正在点菜'
    case 2: return '已点菜'
    case 4: return '正在加菜'
    case 8: return '已加菜'
    case 16: return '已退菜'
    case 32: return '用户已下单'
    case 64: return '用户已加菜'
  }
  return ''

}
/**
 * 获取商品来源颜色
 * @param source 
 * @returns 
 */
const getGoodStatusColor = (source: number) => {
  switch (source) {
    case 1: return 'coral'
    case 2: return 'turquoise'
    case 4: return 'plum'
    case 8: return 'chartreuse'
    case 16: return 'silver'
    case 32: return 'purple'
    case 64: return 'teal'
  }
  return ''
}


/**
 * 获取外卖商品状态描述
 * @param source 
 * @returns 
 */
const getOutOrderGoodStatusText = (source: string) => {
  switch (source) {
    case OutOrderGoodStatus.Normal: return '正常'
    case OutOrderGoodStatus.Refunding: return '正在退款'
    case OutOrderGoodStatus.Refunded: return '已退款'
  }
  return '-'
}
/**
 * 获取外卖商品状态颜色
 * @param source 
 * @returns 
 */
const getOutOrderGoodStatusColor = (source: string) => {
  switch (source) {
    case OutOrderGoodStatus.Normal: return 'blue'
    case OutOrderGoodStatus.Refunding: return 'pink'
    case OutOrderGoodStatus.Refunded: return 'gray'
  }
  return ''
}

/**
 * 获取获取外卖订单状态描述
 * @param source 
 * @returns 
 */
const getOutOrderStatusText = (source: number) => {
  switch (source) {
    case 1: return '已付款'
    case 2: return '已拒单'
    case 4: return '已接单'
    case 8: return '待取货'
    case 16: return '配送中'
    case 32: return '配送异常'
    case 64: return '已送达'
    case 128: return '已收货'
    case 256: return '未付款'
    case 512: return '已评价'
    case 1024: return '已关闭'
    case 2048: return '取消配送'
    case 4096: return '支付超时'
  }
  return ''
}
/**
 * 获取外卖订单状态颜色
 * @param source 
 * @returns 
 */
const getOutOrderStatusColor = (source: number) => {
  switch (source) {
    case 1: return 'coral'
    case 2: return 'gray'
    case 4: return 'plum'
    case 8: return 'chartreuse'
    case 16: return '#81b83d'
    case 32: return 'red'
    case 64: return 'orange'
    case 128: return 'green'
    case 256: return 'gray'
    case 512: return 'green'
    case 1024: return 'gray'
    case 2048: return 'gray'
    case 4096: return 'gray'
  }
  return ''
}

/**
 * 获取商城订单状态描述
 * @param status 
 * @returns 
 */
const getMallOrderStatusText = (status: string) => {
  switch (status) {
    case MallOrderStatus.NotPay: return '未付款'
    case MallOrderStatus.Normal: return '已付款'
    case MallOrderStatus.Finish: return '已完成'
    case MallOrderStatus.Cancel: return '已取消'
    case MallOrderStatus.Refunded: return '已退款'
    case MallOrderStatus.Sended: return '已发货'
    default: return '未知'
  }
}
/**
 * 获取商城订单状态颜色
 * @param source 
 * @returns 
 */
const getMallOrderStatusColor = (status: string) => {
  switch (status) {
    case MallOrderStatus.NotPay: return 'gray'
    case MallOrderStatus.Normal: return 'blue'
    case MallOrderStatus.Finish: return 'green'
    case MallOrderStatus.Cancel: return 'gray'
    case MallOrderStatus.Refunded: return 'gray'
    case MallOrderStatus.Sended: return 'orange'
    default: return '#555'
  }
}

/**
 * 获取活动类别的名称
 * @param type 
 * @returns 
 */
const getActivityTypeText = (type: number) => {
  switch (type) {
    case 1: return '新客立减'
    case 2: return '满减'
    case 3: return '优惠券'
    case 4: return '商品折扣'
    case 5: return '订单折扣'
    case 6: return '整单立减'
    case 7: return '商品券'
  }
  return '未知'
}

/**
 * 获取物流类型的名称
 * @param type 
 * @returns 
 */
const getLogisticsTypeText = (type: LogisticsType) => {
  switch (type) {
    case LogisticsType.Self: return '自配送'
    case LogisticsType.Yichengfeike: return '一城飞客'
  }
  return '未知'
}

/**
 * 获取配送方式的名称
 * @param type 
 * @returns 
 */
const getDeliveryModeText = (type: DeliveryMode) => {
  switch (type) {
    case DeliveryMode.Third: return '商家配送'
    case DeliveryMode.Own: return '自配送'
    case DeliveryMode.Self: return '自提'
  }
}

/**
 * 获取商城订单配送方式的名称
 * @param type 
 * @returns 
 */
const getMallDeliveryModeText = (type: DeliveryMode) => {
  switch (type) {
    case DeliveryMode.Third: return '快递'
    case DeliveryMode.Own: return '自配送'
    case DeliveryMode.Self: return '自提'
  }
}

/**获取餐具数量 */
const getTablewareQuantityDesc = (num: number) => {
  if (num < 0) return '商家依据餐量提供'
  else if (num === 0) return '无需餐具'
  else if (num > 10) return '10份以上'
  return num + '份'
}

/**
 * 登录用户是否拥有指定权限
 * @param auth 
 * @returns 
 */
const hasAuth = (auth: string) => {
  if (!$app.staff) return true            // 直接使用门店帐号登录，无需判断权限
  if ($app.auth?.indexOf(AuthDictionary.manager) > -1) return true
  return $app.auth?.indexOf(auth) > -1    // 指定的权限在权限列表中是否存在
}

/**
 * 转化为金钱
 * @param text 
 * @returns 
 */
const parseMoney = (text: string | number) => '￥' + +(+text).toFixed(2)

/**
 * 暂停
 * @param num 毫秒数
 * @returns 
 */
const sleep = (num: number) => new Promise<void>(resolve => setTimeout(() => resolve(), num))

/**
 * 获取今日时间（Dayjs类型）
 * @param time 时间
 */
const getTodayTime = (time?: string) => {
  const date = dayjs(Date.now()).format('YYYY-MM-DD')
  time ??= '00:00:00'
  return dayjs(date + ' ' + time)
}

/**
 * 图片路径转换
 */
const parseImage = (path: string) => path ?? '/images/empty.png'

/**
 * 根据字典分组，获取其名称
 * @param group 
 * @returns 
 */
const getDicGroupName = (group: DataDicGroup) => {
  if (group === DataDicGroup.StorePayment) return '支付方式'
  else if (group === DataDicGroup.ReturnGoodReason) return '退菜原因'
  return null
}

/**
 * 格式化JSON
 * @param json json数据
 * @returns 
 */
const syntaxHighlight = (json: any) => {
  if (typeof json !== 'string') {
    json = JSON.stringify(json, undefined, 4)
  }
  json = json.replace(/&/g, '&').replace(/</g, '<').replace(/>/g, '>')
  return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, function (match) {
    let cls = 'number'
    if (/^"/.test(match)) {
      if (/:$/.test(match)) {
        cls = 'key'
      } else {
        cls = 'string'
      }
    } else if (/true|false/.test(match)) {
      cls = 'boolean'
    } else if (/null/.test(match)) {
      cls = 'null'
    }
    if (cls == 'key') {
      return '<br>&nbsp;&nbsp;&nbsp;&nbsp;<span class="' + cls + '">' + match + '&nbsp;</span>'
    } else {
      return '<span class="' + cls + '">' + match + '</span>'
    }
  }).replace("}", "<br>}")
}

function deepClone(source, hash = new WeakMap()) {
  if (typeof source !== 'object' || source === null) {
    return source
  }
  if (hash.has(source)) {
    return hash.get(source)
  }
  const target = Array.isArray(source) ? [] : {}
  Reflect.ownKeys(source).forEach(key => {
    const val = source[key]
    if (typeof val === 'object' && val != null) {
      target[key] = deepClone(val, hash)
    } else {
      target[key] = val
    }
  })
  return target
}

/**根据活动类型，获取描述 */
function getActivityDesc(type: string) {
  switch (type) {
    case StoreActivityType.New: return '新客立减活动'
    case StoreActivityType.FullReduce: return '满减活动'
    case StoreActivityType.Coupon: return '优惠券'
    case StoreActivityType.Discount: return '折扣活动'
    case StoreActivityType.Return: return '下单返券'
    case StoreActivityType.Good: return '商品券'
    default:
      return ''
  }
}

/**获取卡券的使用范围描述 */
function getMarketingScopedDesc(type: number) {
  if (!type || type === 0) return '堂食'
  let arr: string[] = []
  if ((type & MarketingScoped.Store) > 0) arr.push('堂食')
  if ((type & MarketingScoped.Out) > 0) arr.push('外卖')
  if ((type & MarketingScoped.Mall) > 0) arr.push('商城')
  return arr.join(',')
}

/**获取卡券的标签描述 */
function getMarketingTagDesc(tags: string[]) {
  if (!tags || tags.length === 0) return ''
  let arr: string[] = []
  if (tags.indexOf(ActivityTag.Birth) > -1) arr.push('生日')
  if (tags.indexOf(ActivityTag.Spring) > -1) arr.push('春节')
  return arr
}

const commonFunc = {
  /**函数节流 */
  throttle(func, delay: number) {
    let timer: any
    return (...params: any[]) => {
      if (!timer) {
        timer = setTimeout(() => {
          func.apply(this, params)
          timer = null
        }, delay)
      }
    }
  },
  /**函数防抖 */
  debounce(func: Function, delay) {
    let timer: any
    return (...params: any[]) => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        func.apply(this, params)
      }, delay)
    }
  }
}

export default {
  encrypt,
  message,
  modal: Modal as ModalApi,
  notify: notification,
  clone,
  required,
  getStoreTypeName,
  compress,
  getBase64,
  genId,
  genGuid,
  loading,
  setLoading,
  parseBirthday,
  parseAge,
  parseGender,
  parseGenderColor,
  parsePost,
  getDate,
  getNowTime,
  getTimespan,
  formatAmount,
  getOrderStatusText,
  getOrderStatusColor,
  getOrderSourceText,
  getOrderSourceColor,
  getGoodStatusText,
  getGoodStatusColor,
  getActivityTypeText,
  getLogisticsTypeText,
  hasAuth,
  parseMoney,
  sleep,
  getTodayTime,
  parseImage,
  syntaxHighlight,
  getDicGroupName,
  deepClone,
  getActivityDesc,
  getOutOrderStatusText,
  getOutOrderStatusColor,
  getDeliveryModeText,
  getTablewareQuantityDesc,
  getOutOrderGoodStatusColor,
  getOutOrderGoodStatusText,
  getMallOrderStatusText,
  getMallOrderStatusColor,
  getMarketingScopedDesc,
  getMallDeliveryModeText,
  getMarketingTagDesc,
  commonFunc
}